import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import Cached from "@mui/icons-material/Cached";
import CalendarToday from "@mui/icons-material/CalendarToday";
import Download from "@mui/icons-material/Download";
import LanguageOutlined from "@mui/icons-material/LanguageOutlined";
import RemoveRedEye from "@mui/icons-material/RemoveRedEye";
import Tooltip from "@mui/material/Tooltip";
import { PreviewConsentFlow, PreviewConsentMode, PreviewRequestType } from "healthex-common/preview.dtos";
import { ScreenshotMetadataStatus } from "healthex-common/screenshot.dtos";
import { dateTimeMinutesOnlyString, filterFalse, filterNull, getAMPM, willNeverHappen } from "healthex-common/utils";
import { observer } from "mobx-react";
import { downloadZipFromUrls } from "../Logic/ZipUtils";
import { AddJoinCommonBannerContainer } from "./AddProjectJoinCommon/AddJoinCommonComponents.styles";
import { FlexRowSpaceBetween, HorizontalDivider } from "./common.styles";
import { ConsentPreviewViewParent, DownloadSectionWrapper, ErrorIcon, ErrorStatus, NoShrinkButton, PDFDownloadButtonCircularProgress, PDFDownloadStyledButton, PreviewButtons, PreviewInfo, PreviewInfoItem, PreviewOrDownloadSection, PreviewOrDownloadText, PreviewOrDownloadTitle, PreviewOrDownloadTitleAndText, PreviewTitle, PreviewTitleAndInfo, ProjectManagerPreviewSection, ReadyGeneratedOnText, ReadyIcon, ReadyStatus, ReadyStatusLine, ReadyText, } from "./ConsentPreviewView.styles";
import { ConsentPreviewViewModel } from "./ConsentPreviewViewModel";
import { HXButton } from "./HXButton";
import { HXDataGrid } from "./HXDataGrid";
import { LighterText, Text } from "./HXDesktopPage.styles";
import { languageDescription } from "./LanguageDescriptions";
import { PatientConsentPreviewDialog } from "./PatientConsentPreviewDialog";
import { useViewModel } from "./useViewModel";
export const ConsentPreviewView = observer((props) => {
    const model = useViewModel(ConsentPreviewViewModel, props);
    return (_jsxs(ConsentPreviewViewParent, { children: [model.variant === "add-project" ? (_jsxs(_Fragment, { children: [_jsxs(PreviewOrDownloadSection, { children: [_jsxs(PreviewOrDownloadTitleAndText, { children: [_jsx(PreviewOrDownloadTitle, { children: "Preview" }), _jsx(PreviewOrDownloadText, { children: model.previewText })] }), previewButtons()] }), _jsx(HorizontalDivider, {})] })) : (_jsxs(ProjectManagerPreviewSection, { children: [_jsxs(PreviewTitleAndInfo, { children: [_jsx(PreviewTitle, { children: "Preview patient experience" }), _jsxs(PreviewInfo, { children: [_jsxs(PreviewInfoItem, { children: [_jsx(CalendarToday, {}), _jsxs(LighterText, { children: ["Active since ", model.activeSince] })] }), _jsxs(PreviewInfoItem, { children: [_jsx(LanguageOutlined, {}), _jsx(LighterText, { children: model.supportedLanguages.map((language) => languageDescription(language)).join(", ") })] })] })] }), previewButtons()] })), _jsxs(DownloadSectionWrapper, { showBorderAndBackground: model.variant === "project-manager", children: [_jsxs(PreviewOrDownloadSection, { children: [_jsxs(FlexRowSpaceBetween, { gap: 40, alignItems: "center", children: [_jsxs(PreviewOrDownloadTitleAndText, { children: [_jsx(PreviewOrDownloadTitle, { children: "PDF generation and download" }), _jsx(PreviewOrDownloadText, { children: model.pdfText })] }), model.variant === "project-manager" && allButton()] }), model.screenshotsNeedingUpdate.length > 0 && (_jsx(AddJoinCommonBannerContainer, { children: _jsxs(FlexRowSpaceBetween, { alignItems: "center", children: [_jsx(Text, { children: "Some information has changed since your PDFs were last generated." }), _jsx(HXButton, { text: true, startIcon: _jsx(Cached, {}), onClick: () => model.generateScreenshots(model.screenshotsNeedingUpdate), children: "Regenerate" })] }) })), model.variant === "add-project" && _jsx("div", { children: allButton() })] }), _jsx(HXDataGrid, { rows: model.previewScreenshots, columns: columns(), getRowId: (status) => status.key, showPagination: false, rowHeight: 81, rowSelection: false })] }), _jsx(PatientConsentPreviewDialog, { container: props.container, open: model.previewConsentDialogOpen, onClose: () => model.hidePreviewConsent() })] }));
    function previewButtons() {
        return (_jsx(PreviewButtons, { children: _jsx(HXButton, { disabled: !props.informedConsentCompleted, secondary: true, startIcon: _jsx(RemoveRedEye, {}), onClick: () => model.previewConsent(PreviewConsentFlow.PATIENT_DIRECTED_DATA_EXCHANGE), children: "Preview patient experience" }) }));
    }
    function columns() {
        return filterFalse([
            model.includeRequestTypes
                ? {
                    field: "requestType",
                    headerName: "Request type",
                    flex: 1,
                    renderCell: (params) => { var _a; return requestTypeDescription((_a = params.row.requestType) !== null && _a !== void 0 ? _a : PreviewRequestType.INITIAL_HEALTH_RECORD_ACCESS); },
                }
                : false,
            {
                field: "language",
                headerName: "Language",
                flex: 1,
                renderCell: (params) => languageDescription(params.row.language),
            },
            model.includeGuided && {
                field: "consentMode",
                headerName: "Consent mode",
                flex: 1,
                renderCell: (params) => consentModeDescription(params.row.consentMode),
            },
            {
                field: "status",
                headerName: "Status",
                flex: 1,
                renderCell: (params) => statusDescription(params.row.status, params.row.generatedAt),
            },
            {
                field: "actions",
                headerName: "Actions",
                flex: 1,
                renderCell: (params) => action(params.row),
            },
        ]);
    }
    function requestTypeDescription(requestType) {
        switch (requestType) {
            case PreviewRequestType.INITIAL_HEALTH_RECORD_ACCESS:
                return "Initial health record access";
            case PreviewRequestType.HEALTH_RECORD_UPDATE:
                return "Health record update";
        }
        return willNeverHappen(requestType);
    }
    function consentModeDescription(consentMode) {
        switch (consentMode) {
            case PreviewConsentMode.REMOTE:
                return "Remote";
            case PreviewConsentMode.GUIDED:
                return "Guided";
        }
        return willNeverHappen(consentMode);
    }
    function statusDescription(status, generatedAt) {
        switch (status) {
            case ScreenshotMetadataStatus.JOB_QUEUED:
            case ScreenshotMetadataStatus.IN_PROGRESS:
                return _jsx(LighterText, { children: "Generating" });
            case ScreenshotMetadataStatus.COMPLETED:
                return (_jsxs(ReadyStatus, { children: [_jsxs(ReadyStatusLine, { children: [_jsx(ReadyIcon, {}), _jsx(ReadyText, { children: "Ready to download" })] }), _jsx(ReadyStatusLine, { children: _jsxs(ReadyGeneratedOnText, { children: ["Generated", " ", generatedAt
                                        ? `${dateTimeMinutesOnlyString(generatedAt).replace(", ", ",\n")}${getAMPM(generatedAt)}`
                                        : "???"] }) })] }));
            case ScreenshotMetadataStatus.ERROR:
                return (_jsxs(ErrorStatus, { children: [_jsx(ErrorIcon, {}), _jsx("div", { children: "Generation failed" })] }));
            case "NOT_GENERATED":
                return _jsx(LighterText, { children: "Not generated yet" });
        }
        return willNeverHappen(status);
    }
    function action(screenshot) {
        return (_jsx(Tooltip, { arrow: true, title: screenshot.disableGeneration ? "You’ll need to complete the translations before you can generate PDFs." : undefined, children: _jsx("div", { children: _jsx(PDFDownloadButton, { disabled: !props.informedConsentCompleted || !!screenshot.disableGeneration, status: screenshot.status, onClick: async () => {
                        if (screenshot.status === ScreenshotMetadataStatus.COMPLETED) {
                            if (screenshot.pdfUrl)
                                window.location.assign(screenshot.pdfUrl);
                        }
                        else if (screenshot.status === "NOT_GENERATED" || screenshot.status === ScreenshotMetadataStatus.ERROR) {
                            await model.generateScreenshots([screenshot]);
                        }
                    } }) }) }));
    }
    function allButton() {
        return (_jsxs(NoShrinkButton, { secondary: true, startIcon: model.allButtonData.type === "DOWNLOAD" ? _jsx(Download, {}) : _jsx(Cached, {}), disabled: !props.informedConsentCompleted || model.allButtonData.type === "GENERATING", onClick: () => allButtonAction(), children: [allButtonDescription(), " (", model.allButtonData.count, ")"] }));
    }
    function allButtonDescription() {
        switch (model.allButtonData.type) {
            case "DOWNLOAD":
                return "Download PDFs";
            case "GENERATE":
                return "Generate remaining PDFs";
            case "GENERATE_ALL":
                return "Generate all PDFs";
            case "RETRY":
                return "Retry generation of failed PDFs";
            case "GENERATING":
                return "Generating PDFs";
            default:
                return willNeverHappen(model.allButtonData);
        }
    }
    async function allButtonAction() {
        switch (model.allButtonData.type) {
            case "DOWNLOAD": {
                const pdfUrls = filterNull(model.previewScreenshots
                    .filter((screenshot) => screenshot.status === ScreenshotMetadataStatus.COMPLETED)
                    .map((screenshot) => { var _a; return (_a = screenshot.pdfUrl) !== null && _a !== void 0 ? _a : null; }));
                if (pdfUrls.length > 0)
                    await downloadZipFromUrls(pdfUrls, `study-preview-informed-consents.zip`);
                break;
            }
            case "GENERATE":
            case "GENERATE_ALL": {
                const toGenerate = model.previewScreenshots.filter((screenshot) => screenshot.status === "NOT_GENERATED" && !screenshot.disableGeneration);
                await model.generateScreenshots(toGenerate);
                break;
            }
            case "RETRY": {
                const toRetry = model.previewScreenshots.filter((screenshot) => screenshot.status === ScreenshotMetadataStatus.ERROR);
                await model.generateScreenshots(toRetry);
                break;
            }
            case "GENERATING":
                break;
            default:
                willNeverHappen(model.allButtonData);
        }
    }
});
export const PDFDownloadButton = observer((props) => {
    const { disabled, status, onClick } = props;
    let startIcon;
    let text;
    switch (status) {
        case ScreenshotMetadataStatus.COMPLETED:
            startIcon = _jsx(Download, {});
            text = "Download as PDF";
            break;
        case ScreenshotMetadataStatus.JOB_QUEUED:
        case ScreenshotMetadataStatus.IN_PROGRESS:
            startIcon = _jsx(PDFDownloadButtonCircularProgress, {});
            text = "Generating...";
            break;
        case ScreenshotMetadataStatus.ERROR:
            startIcon = _jsx(Cached, {});
            text = "Retry";
            break;
        case "NOT_GENERATED":
        case null:
            startIcon = _jsx(Cached, {});
            text = "Generate";
            break;
        default:
            return willNeverHappen(status);
    }
    return (_jsx(PDFDownloadStyledButton, { disabled: disabled, text: true, startIcon: startIcon, onClick: () => onClick(), children: text }));
});
